import React from 'react';
import clsx from 'clsx';
import { FeaturedReleasePills } from '@components/Feed/ListViews/components/FeaturedReleasePills';
import { TotalOtherText } from '@components/Feed/ListViews/components/TotalOtherText';
import { FeedListViewPlaylistLink } from '@components/Feed/ListViews/components/shared';
import { ReleasePlayButton } from '@components/basics/ReleasePlayButton';
import { PlaylistReleasesModal } from '@components/modals/PlaylistReleases';
import { MediaQuery } from '@context/mediaQuery';
import { ModalContext, ModalType } from '@context/modal';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment } from '@graphql/reactQuery';
import {
  MinimalUserFragmentDoc,
  PlaylistActivityInfoFragmentDoc,
  ReleasePillFragmentDoc,
} from '@graphql/reactQuery';
import { SPACE } from '@utils/constants';
import { webappUriPath } from '@utils/url';
import { ListContent } from '../components/ListContent';
import { ListMinimalUser } from '../components/ListMinimalUser';
import { ReleaseInfo } from '../components/ReleaseInfo';
import { ToggledAvatar } from '../components/ToggledAvatar';
import {
  FeedListViewContainer,
  FeedListViewCoverContainer,
  FeedListViewSubText,
  FeedListViewWrapper,
} from '../components/shared';

const TABLET_MAX_PILL = 1;
const DESKTOP_MAX_PILL = 2;
const MOBILE_MAX_RELEASE = 1;
const TABLET_MAX_RELEASE = 2;
export const DESKTOP_MAX_RELEASE = 3;

export const RELEASE_PLAYLIST_ACTION_TYPE = {
  RELEASE_ADDED: 'added',
  RELEASE_REMOVED: 'removed',
} as const;

export type ReleasePlaylistActionType =
  (typeof RELEASE_PLAYLIST_ACTION_TYPE)[keyof typeof RELEASE_PLAYLIST_ACTION_TYPE];

export const ReleasesPlaylistList = ({
  userFrag,
  playlistFrag,
  featuredReleases,
  isoDateString,
  releaseCount,
  actionType,
  actionId,
  ...props
}: {
  actionId: string;
  userFrag: FragmentType<MinimalUserFragmentDoc>;
  playlistFrag: FragmentType<PlaylistActivityInfoFragmentDoc>;
  featuredReleases: FragmentType<ReleasePillFragmentDoc>[] | null;
  isoDateString: string;
  releaseCount: number;
  actionType: ReleasePlaylistActionType;
} & (
  | {
      playlistFeed?: boolean;
    }
  | {
      releaseFeed?: boolean;
    }
)) => {
  const {
    id: playlistId,
    name,
    webappUri,
  } = getFragment(PlaylistActivityInfoFragmentDoc, playlistFrag);

  const playlistFeed = 'playlistFeed' in props ? props.playlistFeed : false;
  const releaseFeed = 'releaseFeed' in props ? props.releaseFeed : false;

  const { laptop, tabletLandscape, tablet, desktop } = MediaQuery.useContainer();
  const forceMobileView = (releaseFeed && laptop && !desktop) || !!playlistFeed;
  const { openModal } = ModalContext.useContainer();

  const hasMoreReleases = forceMobileView
    ? releaseCount > 1
    : laptop
    ? releaseCount > DESKTOP_MAX_RELEASE
    : tabletLandscape
    ? releaseCount > TABLET_MAX_RELEASE
    : releaseCount > 1;

  const featuredReleaseCount = hasMoreReleases
    ? forceMobileView
      ? 1
      : laptop
      ? DESKTOP_MAX_PILL
      : tabletLandscape
      ? TABLET_MAX_PILL
      : 1
    : forceMobileView
    ? MOBILE_MAX_RELEASE
    : laptop
    ? DESKTOP_MAX_RELEASE
    : tabletLandscape
    ? TABLET_MAX_RELEASE
    : MOBILE_MAX_RELEASE;

  const firstRelease = !!featuredReleases
    ? getFragment(ReleasePillFragmentDoc, featuredReleases[0])
    : null;

  const {
    avatar,
    username,
    publicAddress,
    webappUri: userWebappUri,
  } = getFragment(MinimalUserFragmentDoc, userFrag);

  const totalOtherReleases =
    laptop && !forceMobileView ? releaseCount - featuredReleaseCount : releaseCount - 1;

  const mobileCover = !!firstRelease && !!firstRelease.coverImage.url && (
    <FeedListViewCoverContainer forceMobileView={forceMobileView}>
      <ReleasePlayButton playButtonSize={tablet ? 'm' : 's'} releaseFrag={firstRelease} />
    </FeedListViewCoverContainer>
  );

  const user = (
    <FeedListViewWrapper
      margin="first"
      className={clsx(forceMobileView && !!playlistFeed && '[&_h3]:text-base-s')}
    >
      <ListMinimalUser userFrag={userFrag} forceMobileView={forceMobileView} />
    </FeedListViewWrapper>
  );

  const releaseFeedText = <FeedListViewSubText>this Sound</FeedListViewSubText>;

  return (
    <FeedListViewContainer>
      {mobileCover}
      {releaseFeed && (
        <ToggledAvatar
          src={avatar?.url}
          username={username}
          walletAddress={publicAddress}
          userWebappUri={userWebappUri}
          forceMobileView={forceMobileView}
        />
      )}

      <ListContent isoDateString={isoDateString} truncateChildren forceMobileView={forceMobileView}>
        {user}
        <FeedListViewSubText forceMobileView={forceMobileView && !!playlistFeed}>
          {actionType}
        </FeedListViewSubText>
        {SPACE}
        {releaseFeed && releaseFeedText}
        {!releaseFeed && !!featuredReleases && (
          <FeaturedReleasePills
            featuredReleases={featuredReleases.slice(0, featuredReleaseCount)}
            totalOtherReleases={totalOtherReleases}
            hasOtherReleases={hasMoreReleases}
            playlistId={playlistId}
            actionType={actionType}
            actionId={actionId}
            forceMobileView={forceMobileView}
          />
        )}
        {firstRelease &&
          firstRelease.artist?.name &&
          (!tabletLandscape || forceMobileView) &&
          !releaseFeed && (
            <>
              <ReleaseInfo
                key={firstRelease.id}
                releaseTitle={firstRelease?.title}
                releaseUri={firstRelease.webappUri}
                artist={{
                  artistName: firstRelease.artist.name,
                  artistUri: firstRelease.artist.webappUri,
                }}
                forceMobileView={forceMobileView}
              />
            </>
          )}
        {hasMoreReleases && (!tabletLandscape || forceMobileView) && !releaseFeed && (
          <>
            <TotalOtherText
              count={totalOtherReleases}
              forceMobileView={forceMobileView}
              onClick={() => {
                openModal(ModalType.PLAYLIST_ACTIONS, {
                  body: (
                    <PlaylistReleasesModal
                      actionId={actionId}
                      type={actionType}
                      playlistId={playlistId}
                    />
                  ),
                });
              }}
            />
          </>
        )}
        {!playlistFeed && (
          <>
            {SPACE}
            <FeedListViewSubText>{actionType === 'added' ? 'to' : 'from'}</FeedListViewSubText>
            {SPACE}
            <FeedListViewPlaylistLink pathnameUri={webappUriPath(webappUri)}>
              <FeedListViewSubText className="playlist-name" black>
                {name}
              </FeedListViewSubText>
            </FeedListViewPlaylistLink>
          </>
        )}
      </ListContent>
    </FeedListViewContainer>
  );
};
